/**
 * Shared W/L preset catalog. Referenced from the Toolbar and from the hanging-
 * protocol editor — put new presets here so both places see them.
 */

export interface WLPreset {
  label: string;
  ww: number;
  wc: number;
  /** Empty = applies to any modality. */
  modalities: string[];
}

export const WL_PRESETS: WLPreset[] = [
  // CT
  { label: 'Abdomen',        ww: 400,  wc: 40,   modalities: ['CT'] },
  { label: 'Bone',           ww: 2500, wc: 480,  modalities: ['CT'] },
  { label: 'Brain',          ww: 80,   wc: 40,   modalities: ['CT'] },
  { label: 'Chest',          ww: 1500, wc: -600, modalities: ['CT'] },
  { label: 'Lung',           ww: 1500, wc: -600, modalities: ['CT'] },
  { label: 'Liver',          ww: 150,  wc: 30,   modalities: ['CT'] },
  { label: 'Mediastinum',    ww: 350,  wc: 50,   modalities: ['CT'] },
  { label: 'Soft Tissue',    ww: 400,  wc: 40,   modalities: ['CT'] },
  { label: 'Stroke',         ww: 8,    wc: 32,   modalities: ['CT'] },
  { label: 'Temporal Bones', ww: 2800, wc: 600,  modalities: ['CT'] },
  { label: 'Spine',          ww: 300,  wc: 40,   modalities: ['CT', 'MR'] },
  // MR
  { label: 'MR Default',     ww: 500,  wc: 250,  modalities: ['MR'] },
  { label: 'MR Brain T1',    ww: 600,  wc: 300,  modalities: ['MR'] },
  { label: 'MR Brain T2',    ww: 1200, wc: 600,  modalities: ['MR'] },
  // CR / DX
  { label: 'Chest X-Ray',    ww: 2000, wc: 500,  modalities: ['CR', 'DX'] },
  { label: 'Bone X-Ray',     ww: 2500, wc: 700,  modalities: ['CR', 'DX'] },
  // MG
  { label: 'Mammogram',      ww: 2048, wc: 1024, modalities: ['MG'] },
  // Universal
  { label: 'Full Dynamic',   ww: 4096, wc: 2048, modalities: [] },
];

export function findPreset(label: string): WLPreset | undefined {
  return WL_PRESETS.find((p) => p.label === label);
}

/** Fallback W/L when the DICOM header has no WindowCenter/Width and no
 *  user-picked preset. Keeps MPR/3D viewports from rendering solid black
 *  when only raw voxel range is available. */
export function defaultWlForModality(modality?: string): { ww: number; wc: number } {
  switch ((modality || '').toUpperCase()) {
    case 'CT':         return { ww: 400,  wc: 40 };   // soft tissue
    case 'MR':         return { ww: 500,  wc: 250 };
    case 'CR':
    case 'DX':         return { ww: 2000, wc: 500 };
    case 'MG':         return { ww: 2048, wc: 1024 };
    case 'PT':         return { ww: 10,   wc: 5 };    // SUV-ish
    default:           return { ww: 400,  wc: 40 };
  }
}

/** Presets valid for the given modality, plus universal presets. */
export function presetsForModality(modality?: string): WLPreset[] {
  return WL_PRESETS.filter(
    (p) => p.modalities.length === 0 || !modality || p.modalities.includes(modality)
  );
}
